/**
 * Wesco Coatings - Product page logic
 * -----------------------------------
 * - Updates a live summary when a coating is selected
 * - Intercepts Add to Cart submit to add seed + coating in a SINGLE API call
 * - Falls back to native form submit when no coating is selected
 */
(function () {
  'use strict';

  function formatMoney(amountInCents) {
    var dollars = (amountInCents / 100).toFixed(2);
    return '$' + dollars;
  }

  /**
   * Map of coating selector element -> getState function.
   * Used during the Add-to-Cart click to read the selected coating.
   */
  var selectorToState = new WeakMap();

  function init(selector) {
    var unitPriceCents = 0;
    var coatingType = 'none';
    var coatingLabel = '';
    var coatingVariantId = null;

    var summary = selector.querySelector('[data-wesco-coating-summary]');
    var summaryText = selector.querySelector('[data-wesco-coating-summary-text]');
    var inputs = selector.querySelectorAll('[data-wesco-coating-input]');

    var kgPerUnit = parseFloat(selector.dataset.wescoKgPerUnit) || 0;
    var productTitle = selector.dataset.wescoProductTitle || '';
    var productId = selector.dataset.wescoProductId || '';

    /**
     * Find the nearest product form on the page.
     * Strategy: walk up to the nearest <form> ancestor, OR
     * find the form by [x-ref="product_form"] in the same product container.
     */
    function findProductForm() {
      var form = selector.closest('form');
      if (form) return form;

      var container = selector.closest('[x-ref="product_form_container"]') ||
                      selector.closest('.product-form') ||
                      selector.closest('[id^="MainProduct"]') ||
                      document;

      return container.querySelector('form[action*="/cart/add"]') ||
             container.querySelector('form');
    }

    function getCartQuantity() {
      var form = findProductForm();
      if (form) {
        var qtyInput = form.querySelector('input[name="quantity"]:not([type="hidden"]), input.quantity__input');
        if (qtyInput) {
          var v = parseInt(qtyInput.value, 10);
          if (!isNaN(v) && v > 0) return v;
        }
      }
      // Fallback: any quantity input on the page
      var anyQty = document.querySelector('input[name="quantity"]:not([type="hidden"])');
      if (anyQty) {
        var v2 = parseInt(anyQty.value, 10);
        if (!isNaN(v2) && v2 > 0) return v2;
      }
      return 1;
    }

    function getTotalKg() {
      return kgPerUnit * getCartQuantity();
    }

    function updateSummary() {
      if (coatingType === 'none' || unitPriceCents === 0 || kgPerUnit === 0) {
        summary.classList.add('hidden');
        return;
      }
      var totalKg = getTotalKg();
      var totalCents = unitPriceCents * totalKg;
      summary.classList.remove('hidden');
      summaryText.textContent =
        totalKg + 'kg of ' + coatingLabel +
        ' will be added at ' + formatMoney(totalCents) + '.';
    }

    function getCoatingState() {
      return {
        type: coatingType,
        variantId: coatingVariantId,
        label: coatingLabel,
        unitPriceCents: unitPriceCents,
        kgPerUnit: kgPerUnit,
        productTitle: productTitle,
        productId: productId
      };
    }

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        coatingType = input.dataset.wescoCoatingType;

        if (coatingType === 'none') {
          unitPriceCents = 0;
          coatingLabel = '';
          coatingVariantId = null;
        } else {
          unitPriceCents = parseInt(input.dataset.wescoUnitPrice, 10) || 0;
          coatingVariantId = input.dataset.wescoVariantId || null;
          var labelEl = input.parentElement.querySelector('span:not([class])') ||
                        input.parentElement.querySelector('span');
          coatingLabel = labelEl ? labelEl.textContent.trim() : coatingType;
        }

        updateSummary();
      });
    });

    document.addEventListener('input', function (event) {
      if (
        event.target.matches &&
        (event.target.matches('input[name="quantity"]') ||
          event.target.matches('input.quantity__input'))
      ) {
        updateSummary();
      }
    });

    document.addEventListener('click', function (event) {
      if (
        event.target.closest &&
        event.target.closest('.quantity__button, [name="plus"], [name="minus"]')
      ) {
        setTimeout(updateSummary, 50);
      }
    });

    // Register the selector itself so the global click handler can find it.
    selectorToState.set(selector, getCoatingState);
  }

  /**
   * Single-call add to cart: adds seed + coating in one API request.
   */
  function addBothToCart(seedVariantId, seedQuantity, coatingState, form) {
    var totalKg = coatingState.kgPerUnit * seedQuantity;

    var payload = {
      items: [
        {
          id: parseInt(seedVariantId, 10),
          quantity: seedQuantity
        },
        {
          id: parseInt(coatingState.variantId, 10),
          quantity: totalKg,
          properties: {
            /* === WESCO COATING ADDON START === */
            'Coating for': coatingState.productTitle,
            '_item_type': 'coating',
            '_linked_seed_id': String(coatingState.productId),
            '_linked_seed_variant_id': String(seedVariantId)
            /* === WESCO COATING ADDON END === */
          }
        }
      ]
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) {
          return response.text().then(function (txt) {
            throw new Error('Cart add failed (' + response.status + '): ' + txt);
          });
        }
        return response.json();
      })
      .then(function () {
        // Update cart icon bubble + drawer using the theme's helper.
        if (window.Alpine && Alpine.store && Alpine.store('xCartHelper')) {
          var sections = Alpine.store('xCartHelper').getSectionsToRender().map(function (s) {
            return s.id;
          });

          fetch('/?sections=' + sections.join(','), {
            credentials: 'same-origin'
          })
            .then(function (r) { return r.json(); })
            .then(function (sectionsResponse) {
              Alpine.store('xCartHelper').getSectionsToRender().forEach(function (section) {
                var sectionElement = document.querySelector(section.selector);
                if (sectionElement && sectionsResponse[section.id]) {
                  sectionElement.innerHTML = extractSectionInner(
                    sectionsResponse[section.id],
                    section.selector
                  );
                }
              });

              var bubble = document.querySelector('#cart-icon-bubble span');
              if (bubble) {
                Alpine.store('xCartHelper').currentItemCount =
                  parseInt(bubble.innerHTML, 10) || 0;
              }

              if (Alpine.store('xMiniCart') && Alpine.store('xMiniCart').openCart) {
                Alpine.store('xMiniCart').openCart();
              }

              document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
            });
        }
      })
      .catch(function (err) {
        console.error('[Wesco Coatings]', err);
        // Fallback: let user see what failed
        alert('Could not add to cart. Please try again.');
      });
  }

  function extractSectionInner(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var found = parsed.querySelector(selector);
    return found ? found.innerHTML : html;
  }

  /**
   * Capture phase form submit listener.
   * Runs BEFORE Alpine's @submit handler, allowing us to intercept.
   */
  function attachClickListener() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('button[name="add"], [type="submit"][name="add"]');
      if (!btn) return;

      // Find the coating selector on the page (single-product page assumption)
      var selector = document.querySelector('[data-wesco-coating-selector]');
      if (!selector) return; // No coating selector = nothing to do

      var getState = selectorToState.get(selector);
      if (!getState) return;

      var state = getState();
      if (state.type === 'none' || !state.variantId) return; // No coating selected

      // Find the seed variant ID and quantity from the button's nearest scope
      var scope = btn.closest('[x-data], form, section, body');

      var idInput = (scope || document).querySelector('input[name="id"]');
      var qtyInput = (scope || document).querySelector('input[name="quantity"]:not([type="hidden"]), input.quantity__input') ||
                     (scope || document).querySelector('input[name="quantity"]');

      var seedVariantId = idInput ? idInput.value : null;
      var seedQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (isNaN(seedQty) || seedQty < 1) seedQty = 1;

      if (!seedVariantId) {
        console.warn('[Wesco Coatings] Could not find seed variant ID');
        return;
      }

      // Block Alpine's @click="addToCart()" from firing
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      addBothToCart(seedVariantId, seedQty, state, scope);
    }, true); // capture phase: runs BEFORE Alpine's bubble-phase handler
  }

  function initAll() {
    var selectors = document.querySelectorAll('[data-wesco-coating-selector]');
    selectors.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      attachClickListener();
    });
  } else {
    initAll();
    attachClickListener();
  }

  document.addEventListener('shopify:section:load', initAll);
})();